import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { BorrowerData, Ownership } from '../../../types';
import { useGetBorrowerOwnerships } from '../hooks/useOwnershipQueries';
import { useAuth } from '../../auth/context/AuthContext';
import { BorrowerContextType } from '../types/borrower.types';

const BorrowerContext = createContext<BorrowerContextType | undefined>(undefined);

export const BorrowerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user: authUser, isBorrower, isBroker } = useAuth();
  const [borrowerData, setBorrowerDataState] = useState<BorrowerData | null>(null);

  const ownerId = borrowerData?._id || (isBroker ? authUser?._id : undefined);

  const { data: ownershipsData, loading, error: ownershipsError } = useGetBorrowerOwnerships({
    ownerId,
    skip: !ownerId
  });

  // Initialize borrowerData from authUser when available
  useEffect(() => {
    if (authUser && isBorrower) {
      setBorrowerDataState(authUser as BorrowerData);
    }
  }, [authUser, isBorrower]);

  const ownerships: Ownership[] = ownershipsData || [];
  
  // Filter ownerships: email matches borrower email OR state is APPROVED
  const ownershipsFiltered: Ownership[] = useMemo(() => {
    if (!ownerships.length) {
      return [];
    }
    if (isBroker) {
      return ownerships;
    }
    return ownerships.filter((item: Ownership) =>
      item?.email === borrowerData?.email || item.state === "APPROVED"
    );
  }, [ownerships, borrowerData?.email, isBroker]);

  const setBorrowerData = useCallback((data: BorrowerData | null) => {
    setBorrowerDataState(data);
  }, []);

  const loadBorrowerData = useCallback(async () => {
    // This can be extended to fetch borrower data from API if needed
    if (authUser && isBorrower) {
      setBorrowerDataState(authUser as BorrowerData);
    }
  }, [authUser, isBorrower]);

  const loadOwnerships = useCallback(async () => {
    // The query is already running via useGetOwnershipsByCreator
    // This function can be used to trigger a refetch if needed
  }, []);

  const refreshOwnerships = useCallback(async () => {
    // This can trigger a refetch of ownerships
    // For now, the query will automatically refetch when borrowerData changes
  }, []);

  const hasOwnerships = ownershipsFiltered.length > 0;
  const hasBorrowerData = borrowerData !== null;

  const value: BorrowerContextType = {
    borrowerData,
    ownerships,
    ownershipsFiltered,
    isLoading: loading,
    error: ownershipsError || null,
    setBorrowerData,
    loadBorrowerData,
    loadOwnerships,
    refreshOwnerships,
    hasOwnerships,
    hasBorrowerData,
  };

  return (
    <BorrowerContext.Provider value={value}>
      {children}
    </BorrowerContext.Provider>
  );
};

export const useBorrower = (): BorrowerContextType => {
  const context = useContext(BorrowerContext);
  if (context === undefined) {
    throw new Error('useBorrower must be used within a BorrowerProvider');
  }
  return context;
};
